import type { Control, FieldValues } from 'react-hook-form';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import OdinController, { type OdinControllerProps } from '../../dynamic-form/odin-controller.component';
import { Combobox } from './combobox-field';
import type { MultiComboboxProps } from './multi-combobox';
import type { SingleComboboxProps } from './single-combobox';

// `value` is owned by the react-hook-form field, so it is not a caller-facing prop here. `onValueChange` stays as
// the presets declare it, but acts as a side-effect callback: the controller's own write is what stores the value.
type SingleProps<T> = Omit<SingleComboboxProps<T>, 'value'> & { multiple?: false };
type MultiProps<T> = Omit<MultiComboboxProps<T>, 'value'> & { multiple: true };

/** The react-hook-form half of the props. Shared with {@link HfDependentCombobox}, which builds on `HfCombobox`. */
export interface ComboboxFormProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: string;
  defaultValue?: unknown;
  /** Optional field label rendered above the combobox. Associates with the input via `id` when one is set. */
  label?: string;
  readOnly?: boolean;
  required?: DynamicFormRequiredType;
  /**
   * Caller-supplied error message. Takes precedence over the react-hook-form field error, for callers that derive
   * a field's error outside its own RHF validation (e.g. a group-level error fanned out to individual fields).
   */
  error?: string;
  /** Helper text rendered below the combobox and wired to the input via `aria-describedby`. */
  description?: string;
}

// Multi arm first so `T` infers as the item type on `multiple` call sites (see combobox-field.tsx).
export type HfComboboxProps<T, TFieldValues extends FieldValues = FieldValues> = ComboboxFormProps<TFieldValues> &
  (MultiProps<T> | SingleProps<T>);

type ControllerRender<T> = OdinControllerProps<T>['render'];

export const HfCombobox = <T, TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  defaultValue,
  label,
  readOnly = false,
  required = DynamicFormRequiredType.False,
  error,
  description,
  ...modeProps
}: HfComboboxProps<T, TFieldValues>) => {
  const render: ControllerRender<T | T[] | null> = ({ field, fieldState: { error: fieldError } }) => {
    // `name` doubles as the input id / label `htmlFor`, with `id` an optional override.
    const identifier = modeProps.id ? String(modeProps.id) : name;
    const resolvedError = error ?? fieldError?.message;
    const ariaInvalid = resolvedError !== undefined || modeProps['aria-invalid'];

    // `value` / `onValueChange` come after the spread so the controller wins over any caller-supplied handler,
    // which is instead forwarded to `OdinController` as a side effect below.
    return modeProps.multiple ? (
      <Combobox
        {...modeProps}
        id={identifier}
        value={(field.value as T[]) ?? []}
        onValueChange={field.onChange}
        label={label}
        required={required}
        readOnly={readOnly}
        error={resolvedError}
        description={description}
        aria-invalid={ariaInvalid}
      />
    ) : (
      <Combobox
        {...modeProps}
        id={identifier}
        value={(field.value as T | null) ?? null}
        onValueChange={field.onChange}
        label={label}
        required={required}
        readOnly={readOnly}
        error={resolvedError}
        description={description}
        aria-invalid={ariaInvalid}
      />
    );
  };

  return (
    <OdinController
      name={name}
      control={control}
      defaultValue={defaultValue}
      onChange={modeProps.onValueChange as ((value: T | T[] | null) => void) | undefined}
      render={render}
    />
  );
};
